import { useEffect, useMemo, useState } from 'react';
import {
  Button, Divider, Flex, Skeleton,
} from 'antd';
import { Link } from 'react-router-dom';

import dayjs from 'dayjs';
import { Business } from '../../../../store/business';
import User from '../../../Common/User';
import { useAuth } from '../../../../store/auth';
import {
  IconAddress,
  IconClock,
  IconGoogle,
  IconInternet,
  IconPhone,
  IconReport,
} from '../../../Common/Icon';
import { ModalReport } from '../../../Common/ModalReport';
import Carousel from '../../../Common/Carousel';
import { FileData } from '../../../../store/types';

import styles from './index.module.scss';
import { useSyncWithGoogle } from './useSyncWithGoogle';
import ModalClaimBusiness from '../../../Common/ModalClaimBusiness';

interface BusinessCardProps {
  business: Business | null;
  getBusiness: () => void;
}

function BusinessCard({ business, getBusiness }: BusinessCardProps) {
  const { user } = useAuth();
  const {
    syncWithGoogle, loading, response, isLoaded,
  } = useSyncWithGoogle();

  const [isOpenReportModal, setIsOpenReportModal] = useState(false);
  const [isOpenClaimModal, setIsOpenClaimModal] = useState(false);

  const businessTodayOpeningHours: string | null = useMemo(() => {
    const openingHoursWeekdayText = business?.googleMapsInfo?.openingHoursWeekdayText;

    if (!openingHoursWeekdayText?.length) return null;

    const weekdayText = dayjs().format('dddd').toLowerCase();
    const todayOpeningHours = openingHoursWeekdayText.find((el) => el.toLowerCase().includes(weekdayText));

    return todayOpeningHours || null;
  }, [business]);

  useEffect(() => {
    if (response) {
      getBusiness();
    }
  }, [response]);

  if (!business) return <Skeleton />;

  const isMyBusiness = business.users.some(({ id }) => user && id === user.id);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.name}>{business.name}</h3>
          <div className={styles.address}>{business.address}</div>
        </div>

        {business.categories?.length ? (
          <div>
            <Link
              to={`/categories/${business.categories[0].id}${
                business.subCategories.length > 0
                  ? `/${business.subCategories[0].id}`
                  : ''
              }`}
              className={styles.btnCategory}
            >{`${business.categories[0].name}${
              business.subCategories.length > 0
                ? ` / ${business.subCategories[0].header}`
                : ''
            }`}
            </Link>
          </div>
        ) : null}
      </div>

      <div className={styles.content}>
        <Flex justify="space-between" style={{ width: '100%' }}>
          <User user={business.users?.[0]} date={business.createdAt} />

          {/* {isMyBusiness && (
            <Dropdown
              placement="bottomRight"
              menu={{ items: [{ key: '1', label: 'TODO: Edit' }] }}
              trigger={['click']}
            >
              <Button size="small" type="text" style={{ padding: 0 }}>
                <IconCommentMenu />
              </Button>
            </Dropdown>
          )} */}
          {isOpenReportModal && (
            <ModalReport
              isOpen={isOpenReportModal}
              onCancel={() => setIsOpenReportModal(false)}
              createBody={{
                type: 'business',
                businessId: business.id,
              }}
            />
          )}
        </Flex>

        <div style={{ width: '100%' }}>
          <Carousel
            files={
              business.googleMapsInfo?.fileUrls.map(
                (location): FileData => ({
                  location,
                  id: location,
                  mimeType: '',
                  originalName: '',
                }),
              ) || []
            }
          />
        </div>

        <p className={styles.description}>{business.description}</p>

        <Divider className={styles.divider} />

        {!isMyBusiness ? (
          <Flex
            align="center"
            justify="space-between"
            style={{ width: '100%' }}
          >
            {user ? (
              <div className={styles.claimWrapper}>
                <div className={styles.ownerQuestion}>
                  Are you a business owner?
                </div>
                <Button
                  type="text"
                  className={styles.btnClaim}
                  onClick={() => setIsOpenClaimModal(true)}
                >
                  Claim Business
                </Button>

                <ModalClaimBusiness
                  id={business.id}
                  phone={business.phone}
                  isOpen={isOpenClaimModal}
                  onCancel={() => setIsOpenClaimModal(false)}
                />
              </div>
            ) : null}

            <Button className={styles.btnReport} type="text">
              <IconReport />
            </Button>
          </Flex>
        ) : null}

        <div className={styles.businesInfo}>
          {business.address ? (
            <Flex gap={10} align="center">
              <IconAddress />
              <div className={styles.infoText}>{business.address}</div>
            </Flex>
          ) : null}
          {business.phone ? (
            <Flex gap={10} align="center">
              <IconPhone />
              <div className={styles.infoText}>{business.phone}</div>
            </Flex>
          ) : null}
          {businessTodayOpeningHours ? (
            <Flex gap={10} align="center">
              <IconClock />
              <div className={styles.infoText}>{businessTodayOpeningHours}</div>
            </Flex>
          ) : null}
          {business.website ? (
            <Flex gap={10} align="center">
              <IconInternet />
              <a
                href={business.website}
                target="_blank"
                className={styles.infoText}
                rel="noreferrer"
              >
                {business.website}
              </a>
            </Flex>
          ) : null}
        </div>

        <Divider className={styles.divider} />

        <div className={styles.syncWrapper}>
          <div>
            <p className={styles.syncDescription}>
              Is information up to date? Click the &apos;Sync with Google&apos; button to
              update business details from Google Places.
            </p>
            <p className={styles.syncDescription}>
              <span className={styles.bold}>
                Please note, this option can only be used once per day.
              </span>
            </p>
          </div>
          <Button
            type="text"
            className={styles.btnSync}
            loading={loading}
            onClick={() => syncWithGoogle({
              businessId: business.id,
              placeId: business.googleMapsInfo?.placeId || '',
            })}
            disabled={!isLoaded}
          >
            <IconGoogle /> Sync with Google
          </Button>
        </div>
      </div>
    </div>
  );
}
export default BusinessCard;
